import React, { useEffect, useState, useRef, useCallback } from 'react';
import API_URL from '../api'; // Adjusted path for api.js
import { ParallaxProvider, Parallax } from 'react-scroll-parallax';
import { Link, useParams } from 'react-router-dom';

import VideoModal from '../VideoModal'; // Adjusted path for VideoModal

const fontStyle = `
  @font-face {
    font-family: 'ArquitectaBold';
    src: url('/fonts/ArquitectaBold-webfont.eot');
    src: url('/fonts/ArquitectaBold-webfont.eot?#iefix') format('embedded-opentype'),
         url('/fonts/ArquitectaBold-webfont.woff') format('woff'),
         url('/fonts/ArquitectaBold-webfont.ttf') format('truetype'),
         url('/fonts/ArquitectaBold-webfont.svg#arquitecta_boldregular') format('svg');
    font-weight: normal;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'CourierPrime';
    src: url('/fonts/courier_prime-webfont.woff') format('woff');
    font-weight: normal;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'CourierPrimeItalic';
    src: url('/fonts/courier_prime_italic-webfont.woff') format('woff');
    font-weight: normal;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'BebasNeue';
    src: url('/fonts/bebasneue-webfont.woff') format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

// Add font styles to the document
const styleElement = document.createElement('style');
styleElement.textContent = fontStyle;
document.head.appendChild(styleElement);

const MENU_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Directors', href: '/directors' }, // This will be handled by the dropdown
  { label: 'Contact', href: '/contact' },
];

export default function DirectorPage() {
  // Log state on render
  React.useEffect(() => {
    console.log('[DirectorPage Render State] isModalOpen:', isModalOpen, 'selectedVideo:', selectedVideo);
  }); // Log on every render
  const { directorId } = useParams();
  const [directorName, setDirectorName] = useState('');
  const [directorVideos, setDirectorVideos] = useState([]); // Videos for the current director
  // const [allVideos, setAllVideos] = useState([]); // All videos, for modal navigation if needed - Not strictly needed if modal only shows director's videos
  const [loading, setLoading] = useState(true);
  const [directors, setDirectors] = useState([]); // For the dropdown menu
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleVideoClick = useCallback((video) => {
    console.log('[DirectorPage] handleVideoClick triggered. Video:', video);
    if (!video) {
      console.error('[DirectorPage] handleVideoClick called with null/undefined video');
      return;
    }
    
    setSelectedVideo(video);
    const index = directorVideos.findIndex(v => v.id === video.id);
    const newIndex = Math.max(0, index);
    setSelectedVideoIndex(newIndex);
    console.log('[DirectorPage] Calculated index:', index, 'Set selectedVideoIndex to:', newIndex);
    
    // Log the state before and after setting isModalOpen
    console.log('[DirectorPage] State before opening modal - isModalOpen:', false, 'selectedVideo:', video);
    setIsModalOpen(true);
    
    // Use a timeout to log state after the update is processed
    setTimeout(() => {
      console.log('[DirectorPage] State after opening modal - isModalOpen should be true, selectedVideo:', video);
    }, 0);
    
    document.body.style.overflow = 'hidden';
  }, [directorVideos]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedVideo(null);
    document.body.style.overflow = 'unset';
  }, []);

  const handleVideoChangeByIndex = useCallback((newIndex) => {
    if (!directorVideos || directorVideos.length === 0) return;

    // Ensure newIndex is within bounds (though VideoModal should also do this)
    const clampedNewIndex = Math.max(0, Math.min(newIndex, directorVideos.length - 1));

    setSelectedVideoIndex(clampedNewIndex);
    setSelectedVideo(directorVideos[clampedNewIndex]);
  }, [directorVideos]); // selectedVideoIndex is not needed in dependency array as newIndex is passed directly
  
  // Fetch all directors for menu
  useEffect(() => {
    async function fetchAllDirectors() {
      const apiUrl = `${API_URL}/directors`;
      try {
        const res = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          mode: 'cors'
        });
        if (!res.ok) {
          throw new Error(`Failed to fetch directors: ${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        setDirectors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error in fetchAllDirectors:', err);
        setDirectors([]);
      }
    }
    fetchAllDirectors();
  }, []);

  // Fetch videos for the specific director and director's name
  useEffect(() => {
    async function fetchDirectorData() {
      if (!directorId) {
        console.log('DirectorPage: directorId is missing, cannot fetch data.');
        return;
      }
      console.log(`DirectorPage: Fetching data for directorId: ${directorId} (type: ${typeof directorId})`);
      setLoading(true);
      try {
        // Fetch current director's details to get the name
        const directorDetailsRes = await fetch(`${API_URL}/directors/${directorId}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            mode: 'cors'
        });
        if (!directorDetailsRes.ok) {
          throw new Error(`Failed to fetch director details: ${directorDetailsRes.status} ${directorDetailsRes.statusText}`);
        }
        const directorData = await directorDetailsRes.json();
        console.log(`DirectorPage: Director details for ${directorId}:`, directorData);
        setDirectorName(directorData.name || `Director ${directorId}`); // Use ID if name is missing

        // Fetch director-specific videos using the endpoint that supports ordering
        const videosUrl = `${API_URL}/videos?director_id=${directorId}`;
        console.log(`DirectorPage: Attempting to fetch videos from URL: ${videosUrl}`);
        
        const videosRes = await fetch(videosUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            mode: 'cors'
        });
        console.log(`DirectorPage: Videos fetch for directorId ${directorId} - Status: ${videosRes.status}`);

        if (!videosRes.ok) {
          console.error(`DirectorPage: Failed to fetch videos for directorId ${directorId}. Status: ${videosRes.status}, ${await videosRes.text()}`);
          throw new Error(`Failed to fetch videos for director ${directorId}: ${videosRes.status}`);
        }
        
        const directorVideosData = await videosRes.json();
        console.log(`DirectorPage: Videos data for directorId ${directorId}:`, directorVideosData);
        // The backend should already sort by display_order when director_id is provided
        setDirectorVideos(Array.isArray(directorVideosData) ? directorVideosData : []);

      } catch (error) {
        console.error(`DirectorPage: Error in fetchDirectorData for directorId ${directorId}:`, error);
        setDirectorName(`Error Loading Name for ${directorId}`);
        setDirectorVideos([]);
      } finally {
        setLoading(false);
      }
    }
    fetchDirectorData();
  }, [directorId]);
  
  // Menu state and refs (copied from Home.jsx)
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const menuCloseTimeout = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false); // For hamburger button hover
  const [isMenuHovered, setIsMenuHovered] = useState(false); // For menu panel hover
  const [menuHeight, setMenuHeight] = useState(0);
  
  useEffect(() => {
    if (menuRef.current && menuOpen) {
      setMenuHeight(menuRef.current.offsetHeight + 50); // Add padding for visual appeal
    }
  }, [menuOpen, directors]); // Re-calculate if directors list changes, affecting menu height

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuOpen && menuRef.current && menuButtonRef.current &&
          !menuRef.current.contains(event.target) &&
          !menuButtonRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const handleMenuEnter = () => {
    clearTimeout(menuCloseTimeout.current);
    setIsMenuHovered(true);
    setMenuOpen(true);
  };

  const handleMenuLeave = () => {
    setIsMenuHovered(false);
    menuCloseTimeout.current = setTimeout(() => {
      if (!isHovering) setMenuOpen(false); // Only close if not hovering button
    }, 300); // Delay to allow moving cursor between button and menu
  };

  const handleButtonEnter = () => {
    clearTimeout(menuCloseTimeout.current);
    setIsHovering(true);
    setMenuOpen(true);
  };

  const handleButtonLeave = () => {
    setIsHovering(false);
    menuCloseTimeout.current = setTimeout(() => {
      if (!isMenuHovered) setMenuOpen(false); // Only close if not hovering menu
    }, 300);
  };

  return (
    <ParallaxProvider 
      smoothScroll={true} 
      smoothScrollDuration={800}
      scrollAxis="vertical"
    >
      <div style={{ minHeight: '100vh', backgroundColor: '#000' }}> {/* Ensure black background */} 
        {/* Navigation Menu - Adapted for Director Page */}
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: menuOpen ? `translate(-50%, ${menuHeight}px)` : 'translate(-50%, 0)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            transition: 'transform 0.3s ease-in-out',
            transformOrigin: 'center top',
            willChange: 'transform',
            pointerEvents: 'none' // Container itself doesn't catch clicks
          }}
        >
          {/* Hamburger Menu Button */}
          <div 
            ref={menuButtonRef}
            onClick={() => setMenuOpen(!menuOpen)} // Toggle on click
            onMouseEnter={handleButtonEnter}
            onMouseLeave={handleButtonLeave}
            style={{
              cursor: 'pointer',
              width: '24px',
              height: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '5px 0', // Visual padding for click area
              boxSizing: 'content-box',
              pointerEvents: 'auto' // Button catches clicks
            }}
          >
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff',
              transition: 'all 0.3s ease',
              transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'
            }}></span>
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff',
              transition: 'all 0.3s ease',
              opacity: menuOpen ? 0 : 1,
              transform: menuOpen ? 'translateX(20px)' : 'none'
            }}></span>
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff',
              transition: 'all 0.3s ease',
              transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none'
            }}></span>
          </div>

          {/* Director Name Header (instead of TDYK Logo) */}
          <div style={{
            fontFamily: 'CourierPrime, monospace', // Font from Memory
            fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)', // Responsive font size, slightly smaller than Home logo
            color: '#fff',
            textTransform: 'uppercase', // Consistent with other text if desired
            letterSpacing: '1px',
            textShadow: '-1px -1px 0 #000', // Consistent shadow
            pointerEvents: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100vw - 100px)' // Ensure it doesn't push hamburger too far
          }}>
            {directorName || 'Director'}
          </div>
        </div>

        {/* Menu Panel (Copied and adapted from Home.jsx) */}
        <div 
          ref={menuRef}
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            backgroundColor: '#fff', // White background for menu panel
            zIndex: 998, // Below hamburger/logo but above content
            padding: '40px 0 10px', // Top padding for content, bottom for spacing
            opacity: menuOpen ? 1 : 0,
            pointerEvents: menuOpen ? 'auto' : 'none',
            transform: menuOpen ? 'translateY(0)' : 'translateY(-100%)',
            transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out, visibility 0.3s ease-in-out',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden', // Important for controlled expansion
            maxHeight: menuOpen ? '100vh' : '0', // Control height for transition
            visibility: menuOpen ? 'visible' : 'hidden'
          }}
          onMouseEnter={handleMenuEnter}
          onMouseLeave={handleMenuLeave}
        >
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 20px' // Inner padding for menu content
          }}>
            {/* Directors Section in Menu */}
            <div style={{ 
              marginBottom: '15px',
              textAlign: 'center' 
            }}>
              <h3 style={{
                fontSize: '12px',
                fontWeight: 'normal',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                margin: '0 0 15px',
                color: '#666', // Darker text for section title
                fontFamily: 'BebasNeue, sans-serif'
              }}>
                Directors
              </h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '10px'
              }}>
                {directors.map(director => (
                  <Link
                    key={director.id}
                    to={`/directors/${director.id}`}
                    style={{
                      color: director.id === parseInt(directorId) ? '#000' : '#333', // Highlight current director
                      fontWeight: director.id === parseInt(directorId) ? 'bold' : 'normal',
                      textDecoration: 'none',
                      fontSize: '20px', 
                      margin: '0 8px',
                      fontFamily: 'BebasNeue, sans-serif',
                      lineHeight: '1.2',
                      transition: 'all 0.2s ease',
                      opacity: 0.8,
                      ':hover': {
                        opacity: 1,
                        color: '#000'
                      }
                    }}
                    onClick={() => setMenuOpen(false)} // Close menu on click
                  >
                    {director.name}
                  </Link>
                ))}
              </div>
              <div style={{
                width: '100%',
                height: '1px',
                backgroundColor: '#eee', // Light separator line
                margin: '15px auto',
                maxWidth: '500px'
              }}></div>
            </div>

            {/* Main Menu Links (excluding Directors link as it's handled above) */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px 20px',
              marginBottom: '10px',
              justifyContent: 'center',
              maxWidth: '800px',
              marginLeft: 'auto',
              marginRight: 'auto',
              padding: '0 20px'
            }}>
              {MENU_LINKS.filter(link => link.label !== 'Directors').map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  style={{
                    color: 'rgba(0, 0, 0, 0.8)', // Standard link color
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontFamily: 'BebasNeue, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    padding: '3px 0',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    ':hover': {
                      color: '#000',
                      opacity: 1
                    }
                  }}
                  onClick={() => setMenuOpen(false)} // Close menu on click
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content - Director's Videos (Parallax Scroll) */}
        <div style={{ width: '100%', background: '#000', position: 'relative', overflow: 'hidden', paddingTop: '70px' /* Space for fixed header */ }}>
          {loading ? (
            <div style={{ color: '#fff', textAlign: 'center', paddingTop: '50px', fontSize: '28px', minHeight: 'calc(100vh - 70px)' }}>
              Loading Director's Videos...
            </div>
          ) : directorVideos.length === 0 ? (
            <div style={{ color: '#fff', textAlign: 'center', paddingTop: '50px', fontSize: '28px', minHeight: 'calc(100vh - 70px)' }}>
              No videos found for this director.
            </div>
          ) : (
            <div style={{ minHeight: 'calc(100vh - 70px)' }}> {/* Ensure content area respects header */} 
              {directorVideos.map((video, idx) => (
                <div 
                  key={video.id}
                  style={{
                    position: 'relative',
                    width: '100vw',
                    height: '100vh', // Full viewport height for each parallax section
                    margin: 0,
                    padding: 0,
                    zIndex: 10 + idx, // Stacking context for parallax items
                    overflow: 'hidden' // Clip parallax content
                  }}
                >
                  <Parallax 
                    speed={-20} // Parallax effect speed and direction
                    style={{
                      position: 'relative', // Needed for Parallax component
                      width: '100vw',
                      height: '100vh',
                      willChange: 'transform' // Performance hint
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      backgroundImage: video.thumbnail && video.thumbnail.trim()
                        ? `url(${video.thumbnail})`
                        : `url(/content/images/thedevilyouknow.tv/tdyk_logo.png)`, // Fallback image
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      zIndex: 1 // Background image layer
                    }} />
                    <div style={{
                      position: 'relative', // For z-index stacking of text content
                      zIndex: 2, // Text content above background
                      width: '100vw',
                      margin: 0,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100vh',
                      padding: 0
                    }}>
                      <Parallax speed={10}> {/* Inner parallax for text, opposite direction or slower */} 
                        <div 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleVideoClick(video)}
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.2rem', // Spacing between text elements
                            textAlign: 'center',
                            textShadow: '-1px -1px 0 #000', // Text shadow for readability
                            padding: '0.5rem'
                          }}>
                            {/* Title - Courier Prime Italic */}
                            <div style={{
                              fontFamily: 'CourierPrimeItalic, monospace',
                              fontSize: 'clamp(1.6rem, 3.25vw, 2.9rem)',
                              color: '#fff',
                              fontStyle: 'italic',
                              textShadow: '-1px -1px 0 #000',
                              margin: 0
                            }}>
                              {video.title || 'Untitled'}
                            </div>
                            
                            {/* Client - BebasNeue */}
                            {video.client && (
                              <div style={{
                                fontFamily: 'BebasNeue, sans-serif',
                                fontSize: 'clamp(3rem, 9vw, 7rem)',
                                lineHeight: 0.9,
                                color: '#fff',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                textShadow: '-1px -1px 0 #000',
                                margin: 0
                              }}>
                                {video.client}
                              </div>
                            )}
                            {/* Director's name is in the main header, not repeated per video */}
                          </div>
                        </div>
                      </Parallax>
                    </div>
                  </Parallax>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {isModalOpen && selectedVideo && (
        <VideoModal 
          isOpen={isModalOpen}
          video={selectedVideo} 
          videos={directorVideos} 
          currentIndex={selectedVideoIndex}
          onClose={closeModal} 
          onVideoChange={handleVideoChangeByIndex}
        />
      )}
    </ParallaxProvider>
  );
}
